/**
 * WF-Textarea - Multiline text input component for wizard forms
 *
 * A textarea with validation, styling, and form integration.
 * Enter key inserts newline (does NOT submit the form).
 *
 * @example
 * <wf-textarea name="message" label="Message" rows="4" required />
 */

import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { FormInputBase } from './base/form-input-base.js';
import { ValidationController } from '../controllers/ValidationController.js';

@customElement('wf-textarea')
export class WfTextarea extends FormInputBase {

  // ============================================
  // Textarea-specific Properties
  // ============================================

  /**
   * Placeholder text
   */
  @property({ type: String })
  placeholder = '';

  /**
   * Number of visible rows
   */
  @property({ type: Number })
  rows = 4;

  /**
   * Minimum length
   */
  @property({ type: Number })
  minlength?: number;

  /**
   * Maximum length
   */
  @property({ type: Number })
  maxlength?: number;

  /**
   * Whether field is disabled
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * Show character count
   */
  @property({ type: Boolean, attribute: 'show-count' })
  showCount = false;

  // ============================================
  // Abstract Method Implementations
  // ============================================

  protected _getValidationTriggerProperties(): string[] {
    return ['required', 'minlength', 'maxlength'];
  }

  protected override _getFocusableSelector(): string {
    return 'textarea';
  }

  protected _setupValidators(): void {
    this._validators = [];

    if (this.required) {
      this._validators.push(ValidationController.required());
    }

    if (this.minlength !== undefined) {
      this._validators.push(ValidationController.minLength(this.minlength));
    }

    if (this.maxlength !== undefined) {
      this._validators.push(ValidationController.maxLength(this.maxlength));
    }
  }

  // ============================================
  // Textarea-specific Event Handlers
  // ============================================

  private _handleKeydown = (e: KeyboardEvent): void => {
    // Allow Enter to insert newline (don't trigger form navigation)
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  // ============================================
  // Render
  // ============================================

  render() {
    const charCount = this._value.length;
    const isAtLimit = this.maxlength !== undefined && charCount >= this.maxlength;

    return html`
      <div class="wf-field-container">
        ${this._renderLabel()}
        <textarea
          class="wf-textarea ${this._errorMessage ? 'wf-textarea-error' : ''}"
          id="${this.name}"
          name="${this.name}"
          placeholder="${this.placeholder}"
          rows="${this.rows}"
          .value="${this._value}"
          ?disabled="${this.disabled}"
          ?required="${this.required}"
          maxlength="${this.maxlength ?? ''}"
          data-testid="wf-textarea"
          @input="${this._handleInput}"
          @blur="${this._handleBlur}"
          @focus="${this._handleFocus}"
          @keydown="${this._handleKeydown}"
        ></textarea>
        ${this.showCount && this.maxlength
          ? html`<span class="wf-char-count ${isAtLimit ? 'wf-char-limit' : ''}" data-testid="wf-counter">
              ${charCount}/${this.maxlength}
            </span>`
          : ''}
        ${this._renderHint()}
        ${this._renderError()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-textarea': WfTextarea;
  }
}
